import { GtmNoscript } from 'components/scripts/GtmNoscript'
import { IS_PRODUCTION } from 'constants/isDevelopment'
import { GetServerSideProps } from 'next'
import { FC } from 'react'

import { isCuideiHost } from '../technicalSeo/host'

/**
 * Cuidei's own Google Tag Manager container. GA4 (G-W8XLE3JYC2), Google Ads (AW-18528955070) and the
 * cuidei_* dataLayer events are configured in it, so the site must not load gtag.js for those IDs
 * itself (every page_view would be counted twice).
 */
export const CUIDEI_GTM_ID = 'GTM-MPQQ2HX5'

/**
 * Routes that only exist on Cuidei hosts (see technicalSeo/host): the hub behind cuidei.com.br/ and the
 * pathology pages (/<slug>, /<slug>/consulta). They carry ONLY the Cuidei container: no site-wide
 * Omens GTM / Insider (see _app). `page` is the Next route, e.g. /patologia/[slug].
 */
export const isCuideiTagPage = (page: string): boolean => page.startsWith('/patologia/') || page === '/cuidei-hub'

/**
 * getServerSideProps of the shared pages a Cuidei host also serves (/farma/*, the legal pages). A static
 * page has one HTML for every host, so these render per request and tell _document / _app the host.
 */
export const cuideiHostTagProps: GetServerSideProps<{ cuideiHost: boolean }> = async ({ req }) => ({
  props: { cuideiHost: isCuideiHost(req.headers.host) }
})

/** The document carries ONLY the Cuidei container: a Cuidei-only route, or a shared page on a Cuidei host. */
export const isCuideiTagDocument = (page: string, pageProps?: { cuideiHost?: unknown }): boolean =>
  isCuideiTagPage(page) || pageProps?.cuideiHost === true

const snippet = (id: string) =>
  `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${id}');`

/**
 * Rendered by _document (server HTML, also for the ISR landing): the script as high in <head> as
 * possible, the noscript right after <body>. Production only, so local and preview runs never send hits.
 */
export const CuideiTagManagerHead: FC<{ enabled?: boolean }> = ({ enabled = IS_PRODUCTION }) =>
  enabled ? <script id="cuidei-gtm" dangerouslySetInnerHTML={{ __html: snippet(CUIDEI_GTM_ID) }} /> : null

/**
 * Client fallback for the rest of a Cuidei host (the 404 page, stray Omens routes): their static HTML
 * has no Cuidei container, so add it once. No-op when _document already rendered it.
 */
export const ensureCuideiTagManager = (doc: Document = document): void => {
  if (doc.getElementById('cuidei-gtm')) return
  const script = doc.createElement('script')
  script.id = 'cuidei-gtm'
  script.text = snippet(CUIDEI_GTM_ID)
  doc.head.appendChild(script)
}

export const CuideiTagManagerNoscript: FC<{ enabled?: boolean }> = ({ enabled = IS_PRODUCTION }) =>
  enabled ? <GtmNoscript gtmId={CUIDEI_GTM_ID} /> : null
